/**
 * Invite Member Modal
 *
 * Modal for inviting a new member to a company via email.
 * Supports role selection: owner, manager, editor, viewer.
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, AlertCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { invitationsApi } from "@/lib/api";

const inviteSchema = z.object({
  email: z.string().email("Ogiltig e-postadress"),
  role: z.enum(["owner", "manager", "editor", "viewer"]).default("viewer"),
});

type InviteData = z.infer<typeof inviteSchema>;

const ROLE_OPTIONS = [
  { value: "owner", label: "Ägare" },
  { value: "manager", label: "Administratör" },
  { value: "editor", label: "Redigerare" },
  { value: "viewer", label: "Läsbehörighet" },
] as const;

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onInvited: () => void;
}

export function InviteMemberModal({
  isOpen,
  onClose,
  companyId,
  onInvited,
}: InviteMemberModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      role: "viewer",
    },
  });

  const handleInvite = async (data: InviteData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await invitationsApi.create(companyId, {
        email: data.email,
        role: data.role,
      });

      if (response.success) {
        toast.success("Inbjudan skickad!");
        onInvited();
        handleClose();
      } else {
        setSubmitError(
          response.error?.message || "Kunde inte skicka inbjudan"
        );
      }
    } catch (err) {
      console.error("Invite member error:", err);
      setSubmitError("Ett fel uppstod. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError(null);
    reset();
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>

        <div className="pt-8 pb-6 px-8 text-center border-b border-gray-100">
          <div className="mx-auto w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
            <UserPlus className="text-primary-600" size={24} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Bjud in medlem
          </h2>
          <p className="text-gray-600 mt-1">
            Skicka en inbjudan via e-post
          </p>
        </div>

        <form
          onSubmit={handleSubmit(handleInvite)}
          className="p-8 space-y-5"
        >
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle
                className="text-red-600 mt-0.5 flex-shrink-0"
                size={20}
              />
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-postadress *
            </label>
            <input
              type="email"
              {...register("email")}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="namn@foretag.se"
              disabled={isSubmitting}
              autoFocus
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roll
            </label>
            <select
              {...register("role")}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={isSubmitting}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="text-sm text-red-600 mt-1">
                {errors.role.message}
              </p>
            )}
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
            >
              {isSubmitting ? <Spinner size="sm" /> : "Skicka inbjudan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
